import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { publicApi } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { 
  FileText, 
  CheckCircle, 
  XCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';

export default function PublicPresupuestoPage() {
  const { enlace } = useParams();
  const [presupuesto, setPresupuesto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comentario, setComentario] = useState('');
  const [respondiendo, setRespondiendo] = useState(false);

  useEffect(() => {
    const loadPresupuesto = async () => {
      try {
        const response = await publicApi.getPresupuesto(enlace);
        setPresupuesto(response.data);
      } catch (error) {
        console.error('Error loading presupuesto:', error);
        setError('Presupuesto no encontrado o enlace inválido');
      } finally {
        setLoading(false);
      }
    };
    loadPresupuesto();
  }, [enlace]);

  const handleResponder = async (accion) => {
    setRespondiendo(true);
    try {
      await publicApi.responder(enlace, accion, comentario || null);
      setPresupuesto({ ...presupuesto, estado: accion === 'aprobar' ? 'aprobado' : 'rechazado' });
      toast.success(accion === 'aprobar' ? 'Presupuesto aprobado' : 'Presupuesto rechazado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al responder');
    } finally {
      setRespondiendo(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Presupuesto no encontrado
            </h1>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPendiente = presupuesto.estado === 'pendiente';
  const isVencido = presupuesto.estado === 'vencido';

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-presupuesto-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-200 mb-4">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Presupuesto Pro
            </span>
          </div>
        </div>

        {/* Status Banner */}
        {!isPendiente && (
          <Card className={`border-2 ${presupuesto.estado === 'aprobado' ? 'border-emerald-200 bg-emerald-50' : presupuesto.estado === 'rechazado' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
            <CardContent className="p-4 flex items-center justify-center gap-3">
              {presupuesto.estado === 'aprobado' && <CheckCircle className="w-6 h-6 text-emerald-600" />}
              {presupuesto.estado === 'rechazado' && <XCircle className="w-6 h-6 text-red-600" />}
              {isVencido && <Clock className="w-6 h-6 text-amber-600" />}
              <span className={`font-medium ${presupuesto.estado === 'aprobado' ? 'text-emerald-800' : presupuesto.estado === 'rechazado' ? 'text-red-800' : 'text-amber-800'}`}>
                Este presupuesto está {getStatusLabel(presupuesto.estado).toLowerCase()}
              </span>
            </CardContent>
          </Card>
        )}

        {/* Quote */}
        <Card className="shadow-lg">
          <CardContent className="p-0">
            <div className="quote-preview border-0 shadow-none">
              <div className="quote-header bg-slate-900 text-white rounded-t-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {presupuesto.empresa?.nombre_comercial || 'Empresa'}
                    </h3>
                    {presupuesto.empresa?.direccion && (
                      <p className="text-slate-300 text-sm mt-1">{presupuesto.empresa.direccion}</p>
                    )}
                    {presupuesto.empresa?.telefono && (
                      <p className="text-slate-300 text-sm">{presupuesto.empresa.telefono}</p>
                    )}
                    {presupuesto.empresa?.cif_nif && (
                      <p className="text-slate-300 text-sm">CIF/NIF: {presupuesto.empresa.cif_nif}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-slate-300 text-sm">Presupuesto</p>
                    <p className="font-mono text-lg">{presupuesto.numero}</p>
                    <p className="text-slate-300 text-sm mt-2">
                      {formatDate(presupuesto.created_at)}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-b border-slate-200 bg-slate-50">
                <p className="text-sm text-slate-500 mb-1">Presupuesto para:</p>
                <p className="font-semibold text-slate-900 text-lg">{presupuesto.cliente?.nombre}</p>
                {presupuesto.cliente?.empresa_cliente && (
                  <p className="text-slate-600">{presupuesto.cliente.empresa_cliente}</p>
                )}
              </div>
              
              <div className="quote-items p-6">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-left py-3 text-slate-500 text-sm font-semibold">Concepto</th>
                      <th className="text-right py-3 text-slate-500 text-sm font-semibold">Cant.</th>
                      <th className="text-right py-3 text-slate-500 text-sm font-semibold">Precio</th>
                      <th className="text-right py-3 text-slate-500 text-sm font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {presupuesto.items?.map((item, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="py-4 pr-4">{item.descripcion}</td>
                        <td className="py-4 text-right">{item.cantidad}</td>
                        <td className="py-4 text-right">{formatCurrency(item.precio_unitario)}</td>
                        <td className="py-4 text-right font-medium">{formatCurrency(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="quote-footer p-6 bg-slate-50">
                <div className="flex justify-end">
                  <div className="w-72 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="font-medium">{formatCurrency(presupuesto.subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">IVA (21%)</span>
                      <span className="font-medium">{formatCurrency(presupuesto.iva)}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold pt-3 border-t-2 border-slate-200">
                      <span>Total</span>
                      <span className="text-blue-600">{formatCurrency(presupuesto.total)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 pt-6 border-t border-slate-200 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500 mb-1">Validez</p>
                    <p className="font-medium">{presupuesto.validez_dias} días desde la fecha de emisión</p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-1">Forma de pago</p>
                    <p className="font-medium">{presupuesto.forma_pago}</p>
                  </div>
                </div>
                
                {presupuesto.notas && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-slate-500 text-sm mb-1">Notas</p>
                    <p className="text-slate-700">{presupuesto.notas}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Response Form */}
        {isPendiente && (
          <Card className="shadow-lg">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Responder al presupuesto
              </h3>
              <Textarea
                placeholder="Añade un comentario (opcional)..."
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                rows={3}
                data-testid="public-comentario"
              />
              <div className="flex gap-4">
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-12"
                  onClick={() => handleResponder('aprobar')}
                  disabled={respondiendo}
                  data-testid="public-aprobar-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Aprobar Presupuesto
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50 h-12"
                  onClick={() => handleResponder('rechazar')}
                  disabled={respondiendo}
                  data-testid="public-rechazar-btn"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Rechazar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-slate-500 py-4">
          Presupuesto enviado con{' '}
          <a href="/" className="text-blue-600 hover:underline font-medium">
            Presupuesto Pro
          </a>
        </div>
      </div>
    </div>
  );
}
